import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {finalize, Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {EventExpenseDTO, EventService} from '../api/event-api.service';
import {UserApiService} from '../api/user-api.service';
import {DataTableDirective} from 'angular-datatables';
import {ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';

declare const $: any;

export interface SpenderTotal {
    id: string;
    name: string;
    email: string;
    count: number;
    total: number;
    percent: number;
    color: string;
    /** stroke-dashoffset for this slice of the donut (circumference 100, starting at 12 o'clock). */
    offset: number;
}

@Component({
    selector: 'app-event-expenses',
    templateUrl: './event-expenses.component.html',
    styleUrls: ['./event-expenses.component.css']
})
export class EventExpensesComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(DataTableDirective) dtElement!: DataTableDirective;
    @ViewChild('catTpl', {static: true}) catTpl!: TemplateRef<any>;
    @ViewChild('amountTpl', {static: true}) amountTpl!: TemplateRef<any>;
    @ViewChild('spentByTpl', {static: true}) spentByTpl!: TemplateRef<any>;
    @ViewChild('addedByTpl', {static: true}) addedByTpl!: TemplateRef<any>;
    @ViewChild('addedDateTpl', {static: true}) addedDateTpl!: TemplateRef<any>;
    @ViewChild('receiptTpl', {static: true}) receiptTpl!: TemplateRef<any>;
    @ViewChild('actionsTpl', {static: true}) actionsTpl!: TemplateRef<any>;

    dtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    dtTrigger: Subject<any> = new Subject();

    @Input() eventId?: string;
    @Input() chapterId?: string;

    // Registration info for balance sheet
    @Input() adultCount = 0;
    @Input() childCount = 0;
    @Input() adultFee = 0;
    @Input() childFee = 0;
    @Input() seniorCount = 0;
    @Input() seniorFee = 0;
    @Input() status!: string;
    @Input() donationTotal: number = 0;

    private readonly draftPrefix = 'draft-';
    private readonly maxReceiptSizeBytes = 10 * 1024 * 1024; // 10 MB
    private readonly allowedReceiptExtensions = new Set(['pdf', 'jpg', 'jpeg', 'png']);

    expenses: EventExpenseDTO[] = [];
    loading = false;
    uploadingReceipt = false;

    selectedExpense: EventExpenseDTO | null = null;
    selectedReceiptFile: File | null = null;
    selectedReceiptPreviewUrl: string | null = null;
    newExpense: EventExpenseDTO = {category: '', amount: 0, spentBy: ''};

    // Chapter members shown in the "Spent By" picker; id -> display name.
    expenseMembers: { id: string; name: string; email: string; label: string }[] = [];
    receiptThumbnailUrls: {[key: string]: string} = {};
    loadingThumbnailKeys = new Set<string>();

    constructor(
        private eventService: EventService,
        private userApiService: UserApiService,
        private toastr: ToastrService,
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {
    }

    ngOnInit(): void {
        // Built here, not in ngAfterViewInit: the first DataTables init happens right after the view is created, and
        // it must already see these columns (an init with the empty default options crashes on the 7-column header).
        this.dtOptions = ranaDtOptions({
            // DataTables builds the cells' embedded views while it draws, which can happen outside Angular's change
            // detection (e.g. from a panel-shown event or a deferred init). Without a pass afterwards their bindings
            // stay empty until the next click, so run one after every draw.
            drawCallback: () => this.ngZone.run(() => this.cdr.detectChanges()),
            columns: [
                {title: 'Category', data: null, ngTemplateRef: {ref: this.catTpl, context: {captureEvents: () => {}}}},
                {title: 'Amount', data: null, className: 'text-end', ngTemplateRef: {ref: this.amountTpl, context: {captureEvents: () => {}}}},
                {title: 'Spent By', data: null, ngTemplateRef: {ref: this.spentByTpl, context: {captureEvents: () => {}}}},
                {title: 'Added By', data: null, ngTemplateRef: {ref: this.addedByTpl, context: {captureEvents: () => {}}}},
                {title: 'Added Date', data: null, ngTemplateRef: {ref: this.addedDateTpl, context: {captureEvents: () => {}}}},
                {title: 'Receipt', data: null, orderable: false, ngTemplateRef: {ref: this.receiptTpl, context: {captureEvents: () => {}}}},
                {title: 'Actions', data: null, orderable: false, className: 'text-center', ngTemplateRef: {ref: this.actionsTpl, context: {captureEvents: () => {}}}}
            ]
        });
        this.loadExpenses();
        this.loadMembers();
    }

    ngAfterViewInit(): void {
        this.refreshTableData();

        // This component lives in a collapsed accordion panel, and DataTables can't size/draw a table inside
        // a hidden container. Rebuild it once the panel is actually shown.
        const panel = this.host.nativeElement.closest('.collapse');
        if (panel && typeof $ !== 'undefined') {
            // A short delay, as the registrations tables use: the <tfoot>'s *ngIf and the panel's own transition
            // can still be changing the table's DOM on the event, and DataTables must init after they settle.
            $(panel).on('shown.bs.collapse.eventExpenses', () => setTimeout(() => this.refreshTableData(), 50));
            this.collapsePanel = panel;
        }
    }

    private collapsePanel: Element | null = null;

    // Only array-level changes (add/remove/replace a line item) need this — editing a row
    // in place (selectedExpense) is a normal Angular binding change inside an already-created
    // ngTemplateRef embedded view, which Angular's own change detection already keeps in sync
    // without DataTables needing to redraw anything.
    private redrawQueued = false;

    private refreshTableData(): void {
        this.dtOptions.data = this.expenses;
        // Initial render, data load and panel-shown can all ask in the same moment; two overlapping destroy/init
        // cycles make DataTables warn "Cannot reinitialise DataTable". One queued redraw reads the data set above.
        if (this.redrawQueued) return;
        this.redrawQueued = true;
        setTimeout(() => {
            this.redrawQueued = false;
            this.redrawTable();
        });
    }

    private redrawTable(): void {
        if (this.dtTrigger.closed) return; // the component was destroyed while the redraw was queued
        if (this.dtElement?.dtInstance) {
            // A table whose earlier init failed (e.g. inside a hidden panel) has a rejected dtInstance: start it
            // fresh instead of waiting on a destroy that can never happen.
            this.dtElement.dtInstance.then(
                dt => {
                    dt.destroy();
                    this.dtTrigger.next(null);
                },
                () => this.dtTrigger.next(null)
            );
        } else {
            this.dtTrigger.next(null);
        }
    }

    loadMembers(): void {
        if (!this.chapterId) return;
        this.userApiService.getUsersByChapterId(this.chapterId).subscribe({
            next: (response) => {
                this.expenseMembers = (response || [])
                    .map((u: any) => {
                        const email = u.email || u.emailId || '';
                        const name = `${u.firstname || ''} ${u.lastname || ''}`.trim() || email || u.id;
                        // Several members can share a name, so the picker also shows the email to tell them apart.
                        return {id: u.id, name, email, label: email && email !== name ? `${name} (${email})` : name};
                    })
                    .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
            },
            error: (err) => {
                console.error('Error loading chapter members', err);
                this.toastr.error('Failed to load member list for expenses');
            }
        });
    }

    getMemberEmail(userId?: string): string {
        return (userId && this.expenseMembers.find(m => m.id === userId)?.email) || '';
    }

    getMemberName(userId?: string): string {
        if (!userId) return '--';
        return this.expenseMembers.find(m => m.id === userId)?.name || userId;
    }

    ngOnDestroy(): void {
        if (this.collapsePanel && typeof $ !== 'undefined') {
            $(this.collapsePanel).off('shown.bs.collapse.eventExpenses');
        }
        this.clearSelectedReceiptPreview();
        Object.values(this.receiptThumbnailUrls)
            .filter(url => url.startsWith('blob:'))
            .forEach(url => URL.revokeObjectURL(url));
        this.dtTrigger.unsubscribe();
    }

    loadExpenses(): void {
        if (!this.eventId) return;
        this.loading = true;
        this.eventService.getExpenses(this.eventId)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (data) => {
                    this.expenses = data || [];
                    this.refreshTableData();
                    this.preloadReceiptThumbnails();
                },
                error: (err) => {
                    console.error('Error loading expenses', err);
                    this.toastr.error('Failed to load expenses');
                }
            });
    }

    onReceiptSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        if (!file) {
            this.selectedReceiptFile = null;
            this.clearSelectedReceiptPreview();
            return;
        }

        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        if (!this.allowedReceiptExtensions.has(extension)) {
            this.toastr.error('Only PDF, JPG, JPEG, PNG files are allowed');
            input.value = '';
            this.selectedReceiptFile = null;
            this.clearSelectedReceiptPreview();
            return;
        }

        if (file.size > this.maxReceiptSizeBytes) {
            this.toastr.error('Receipt file must be 10 MB or smaller');
            input.value = '';
            this.selectedReceiptFile = null;
            this.clearSelectedReceiptPreview();
            return;
        }

        this.selectedReceiptFile = file;
        this.clearSelectedReceiptPreview();
        if (this.isImageReceipt(file.name, file.type)) {
            this.selectedReceiptPreviewUrl = URL.createObjectURL(file);
        }
    }

    clearSelectedReceipt(): void {
        this.selectedReceiptFile = null;
        this.clearSelectedReceiptPreview();
        const input = document.getElementById('expenseReceiptInput') as HTMLInputElement | null;
        if (input) input.value = '';
    }

    uploadReceiptAndCreateLine(): void {
        if (!this.eventId) {
            this.toastr.warning('No event selected');
            return;
        }
        if (!this.selectedReceiptFile) {
            this.toastr.warning('Please choose a receipt file first');
            return;
        }
        if (this.status === 'CLOSED') {
            this.toastr.warning('Cannot add expenses to a closed event');
            return;
        }
        if (this.expenses.some(e => this.isDraftExpense(e))) {
            this.toastr.warning('Please save or cancel the current draft expense first');
            return;
        }

        this.uploadingReceipt = true;
        this.eventService.uploadExpenseReceipt(this.eventId, this.selectedReceiptFile)
            .pipe(finalize(() => (this.uploadingReceipt = false)))
            .subscribe({
                next: (uploadRes) => {
                    const draftExpense: EventExpenseDTO = {
                        expenseId: `${this.draftPrefix}${Date.now()}`,
                        category: '',
                        amount: 0,
                        addedBy: sessionStorage.getItem('userId') || '',
                        addedDate: uploadRes.uploadedDate,
                        receiptFileName: uploadRes.filename,
                        receiptContentType: uploadRes.contentType,
                        receiptUploadedDate: uploadRes.uploadedDate
                    };

                    this.expenses.unshift(draftExpense);
                    this.refreshTableData();
                    this.ensureReceiptThumbnail(draftExpense);
                    this.selectedExpense = {...draftExpense};
                    this.selectedReceiptFile = null;
                    this.clearSelectedReceiptPreview();
                    this.toastr.success('Receipt uploaded. Enter category and amount, then save.');
                },
                error: (err) => {
                    console.error('Error uploading receipt', err);
                    this.toastr.error('Failed to upload receipt');
                }
            });
    }

    addExpense(): void {
        if (!this.eventId) {
            this.toastr.warning('No event selected');
            return;
        }
        if (this.status === 'CLOSED') {
            this.toastr.warning('Cannot add expenses to a closed event');
            return;
        }
        if (this.hasOpenDraft()) {
            this.toastr.warning('Please save or cancel the current draft expense first');
            return;
        }
        if (!this.newExpense.category?.trim() || !this.newExpense.amount || this.newExpense.amount <= 0) {
            this.toastr.warning('Please provide category and a valid amount');
            return;
        }

        const payload: EventExpenseDTO = {
            category: this.newExpense.category.trim(),
            amount: Number(this.newExpense.amount),
            spentBy: this.newExpense.spentBy || undefined
        };

        this.eventService.addExpense(this.eventId, payload).subscribe({
            next: (expense) => {
                this.expenses.unshift(expense);
                this.refreshTableData();
                this.newExpense = {category: '', amount: 0, spentBy: ''};
                this.toastr.success('Expense added');
            },
            error: (err) => {
                console.error('Error adding expense', err);
                this.toastr.error('Failed to add expense');
            }
        });
    }

    editExpense(expense: EventExpenseDTO): void {
        this.selectedExpense = {...expense};
    }

    cancelEdit(): void {
        if (this.selectedExpense && this.isDraftExpense(this.selectedExpense)) {
            this.expenses = this.expenses.filter(e => e.expenseId !== this.selectedExpense?.expenseId);
            this.refreshTableData();
        }
        this.selectedExpense = null;
    }

    updateExpense(): void {
        if (!this.eventId || !this.selectedExpense) return;
        if (!this.selectedExpense.category?.trim() || !this.selectedExpense.amount || this.selectedExpense.amount <= 0) {
            this.toastr.warning('Please provide category and a valid amount');
            return;
        }

        const isDraft = this.isDraftExpense(this.selectedExpense);

        if (isDraft) {
            const draftId = this.selectedExpense.expenseId;
            const payload: EventExpenseDTO = {
                category: this.selectedExpense.category.trim(),
                amount: Number(this.selectedExpense.amount),
                spentBy: this.selectedExpense.spentBy || undefined,
                receiptFileName: this.selectedExpense.receiptFileName,
                receiptContentType: this.selectedExpense.receiptContentType,
                receiptUploadedDate: this.selectedExpense.receiptUploadedDate,
                addedBy: this.selectedExpense.addedBy
            };

            this.eventService.addExpense(this.eventId, payload).subscribe({
                next: (created) => {
                    const idx = this.expenses.findIndex(e => e.expenseId === draftId);
                    if (idx > -1) this.expenses[idx] = created;
                    this.refreshTableData();
                    this.cleanupReceiptThumbnailByKey(draftId || '');
                    this.ensureReceiptThumbnail(created);
                    this.selectedExpense = null;
                    this.toastr.success('Expense added');
                },
                error: (err) => {
                    console.error('Error creating expense from receipt draft', err);
                    this.toastr.error('Failed to save expense');
                }
            });
            return;
        }

        if (!this.selectedExpense.expenseId) return;
        this.eventService.updateExpense(this.eventId, this.selectedExpense.expenseId, this.selectedExpense)
            .subscribe({
                next: (updated) => {
                    const idx = this.expenses.findIndex(e => e.expenseId === updated.expenseId);
                    if (idx > -1) this.expenses[idx] = updated;
                    this.refreshTableData();
                    this.selectedExpense = null;
                    this.toastr.success('Expense updated');
                },
                error: (err) => {
                    console.error('Error updating expense', err);
                    this.toastr.error('Failed to update expense');
                }
            });
    }

    deleteExpense(expenseId?: string): void {
        if (!expenseId) return;

        if (expenseId.startsWith(this.draftPrefix)) {
            this.expenses = this.expenses.filter(e => e.expenseId !== expenseId);
            this.refreshTableData();
            this.cleanupReceiptThumbnailByKey(expenseId);
            if (this.selectedExpense?.expenseId === expenseId) {
                this.selectedExpense = null;
            }
            return;
        }

        if (!this.eventId) return;
        this.eventService.deleteExpense(this.eventId, expenseId)
            .subscribe({
                next: () => {
                    const removedExpense = this.expenses.find(e => e.expenseId === expenseId);
                    this.expenses = this.expenses.filter(e => e.expenseId !== expenseId);
                    this.refreshTableData();
                    this.cleanupReceiptThumbnail(removedExpense);
                    this.toastr.success('Expense deleted');
                },
                error: (err) => {
                    console.error('Error deleting expense', err);
                    this.toastr.error('Failed to delete expense');
                }
            });
    }

    viewReceipt(expense: EventExpenseDTO): void {
        if (!this.eventId || !expense.receiptFileName) return;

        this.eventService.downloadExpenseReceipt(this.eventId, expense.receiptFileName)
            .subscribe({
                next: (blob: Blob) => {
                    this.saveBlob(blob, expense.receiptFileName!);
                },
                error: (err) => {
                    console.error('Error downloading receipt', err);
                    this.toastr.error('Failed to download receipt');
                }
            });
    }

    getDisplayDate(expense: EventExpenseDTO): string {
        const date = this.selectedExpense?.expenseId === expense.expenseId
            ? (this.selectedExpense?.addedDate || this.selectedExpense?.receiptUploadedDate)
            : (expense.addedDate || expense.receiptUploadedDate);
        if (!date) return '--';
        try {
            // The API stores dates as "dd/MM/yyyy:HH:mm:ss" (e.g. "24/10/2026:21:43:54"), which new Date() can't read.
            const m = /^(\d{2})\/(\d{2})\/(\d{4}):(\d{2}):(\d{2}):(\d{2})$/.exec(date);
            const parsedDate = m ? new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]) : new Date(date);
            return Number.isNaN(parsedDate.getTime()) ? date : parsedDate.toLocaleString();
        } catch {
            return date;
        }
    }

    isDraftExpense(expense: EventExpenseDTO): boolean {
        return !!expense.expenseId?.startsWith(this.draftPrefix);
    }

    hasOpenDraft(): boolean {
        return this.expenses.some(e => this.isDraftExpense(e));
    }

    isImageReceipt(filename?: string, contentType?: string): boolean {
        if (contentType?.startsWith('image/')) return true;
        const extension = filename?.split('.').pop()?.toLowerCase();
        return extension === 'jpg' || extension === 'jpeg' || extension === 'png';
    }

    getReceiptThumbnailUrl(expense: EventExpenseDTO): string | null {
        const key = this.getThumbnailKey(expense);
        return key && this.receiptThumbnailUrls[key] ? this.receiptThumbnailUrls[key] : null;
    }

    private preloadReceiptThumbnails(): void {
        this.expenses
            .filter(expense => !!expense.receiptFileName && this.isImageReceipt(expense.receiptFileName, expense.receiptContentType))
            .forEach(expense => this.ensureReceiptThumbnail(expense));
    }

    private ensureReceiptThumbnail(expense: EventExpenseDTO): void {
        if (!this.eventId || !expense.receiptFileName) return;
        if (!this.isImageReceipt(expense.receiptFileName, expense.receiptContentType)) return;

        const key = this.getThumbnailKey(expense);
        if (!key || this.receiptThumbnailUrls[key] || this.loadingThumbnailKeys.has(key)) return;

        this.loadingThumbnailKeys.add(key);

        this.eventService.downloadExpenseReceipt(this.eventId, expense.receiptFileName)
            .pipe(finalize(() => this.loadingThumbnailKeys.delete(key)))
            .subscribe({
                next: (blob: Blob) => {
                    this.receiptThumbnailUrls[key] = URL.createObjectURL(blob);
                },
                error: (err) => {
                    console.error('Error loading receipt thumbnail', err);
                }
            });
    }

    private getThumbnailKey(expense: EventExpenseDTO): string {
        if (expense.expenseId) return expense.expenseId;
        return `${expense.eventId || this.eventId || ''}_${expense.receiptFileName || ''}`;
    }

    private cleanupReceiptThumbnail(expense?: EventExpenseDTO): void {
        if (!expense) return;
        this.cleanupReceiptThumbnailByKey(this.getThumbnailKey(expense));
    }

    private cleanupReceiptThumbnailByKey(key: string): void {
        if (!key) return;
        const url = this.receiptThumbnailUrls[key];
        if (url?.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
        delete this.receiptThumbnailUrls[key];
        this.loadingThumbnailKeys.delete(key);
    }

    private clearSelectedReceiptPreview(): void {
        if (this.selectedReceiptPreviewUrl) {
            URL.revokeObjectURL(this.selectedReceiptPreviewUrl);
            this.selectedReceiptPreviewUrl = null;
        }
    }

    private forceDownload(url: string, filename: string): void {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    private saveBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        this.forceDownload(url, filename);
        window.URL.revokeObjectURL(url);
    }

    // Chart colours for the reimbursement summary (cycled when there are more people than colours).
    readonly spenderColors = ['#ff6a00', '#1f4e8c', '#2e9e6b', '#8e44ad', '#d4a017', '#c0392b', '#16a2b8', '#7f8c8d'];

    // Who fronted the money and is owed it back: expenses grouped by "Spent By", biggest first. Unsaved draft lines
    // are ignored. Expenses with nobody chosen are not owed to any member, so they are reported separately.
    get spenderTotals(): SpenderTotal[] {
        const byPerson = new Map<string, { count: number; total: number }>();
        for (const e of this.expenses) {
            if (this.isDraftExpense(e) || !e.spentBy) continue;
            const entry = byPerson.get(e.spentBy) || {count: 0, total: 0};
            entry.count += 1;
            entry.total += Number(e.amount) || 0;
            byPerson.set(e.spentBy, entry);
        }
        const owedTotal = Array.from(byPerson.values()).reduce((sum, v) => sum + v.total, 0);
        let cumulative = 0;
        return Array.from(byPerson.entries())
            .sort((a, b) => b[1].total - a[1].total)
            .map(([id, v], index) => {
                const percent = owedTotal > 0 ? (v.total / owedTotal) * 100 : 0;
                const row: SpenderTotal = {
                    id,
                    name: this.getMemberName(id),
                    email: this.getMemberEmail(id),
                    count: v.count,
                    total: Math.round(v.total * 100) / 100,
                    percent,
                    color: this.spenderColors[index % this.spenderColors.length],
                    offset: 25 - cumulative
                };
                cumulative += percent;
                return row;
            });
    }

    // Total owed back to members.
    get totalOwedToMembers(): number {
        return Math.round(this.spenderTotals.reduce((sum, s) => sum + s.total, 0) * 100) / 100;
    }

    // Expenses with no "Spent By" (e.g. paid straight from chapter funds): nobody to repay.
    get unassignedTotal(): number {
        const sum = this.expenses
            .filter(e => !this.isDraftExpense(e) && !e.spentBy)
            .reduce((total, e) => total + (Number(e.amount) || 0), 0);
        return Math.round(sum * 100) / 100;
    }

    trackBySpender(_index: number, s: SpenderTotal): string {
        return s.id;
    }

    // Total of all expenses
    get totalExpenses(): number {
        return this.expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    }

    // Total collection from registrations
    get totalCollection(): number {
        const ticketsTotal = (this.adultCount * this.adultFee) + (this.childCount * this.childFee)
            + (this.seniorCount * this.seniorFee);
        return ticketsTotal + (this.donationTotal || 0);
    }

    // Net profit/loss
    get netBalance(): number {
        return this.totalCollection - this.totalExpenses;
    }

    get isProfit(): boolean {
        return this.netBalance >= 0;
    }
}
